import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ContentApp from '../../../../components/ContentApp';
import ContentBottom from '../../../../components/ContentBottom';
import InputFormBorder from '../../../../components/Common/InputFormBorder/InputFormBorder';
import DatePickerBorder from '../../../../components/Common/InputFormBorder/DatePickerBorder';
import ModalSelector from '../../../../components/ModalSelector/ModalSelector';
import TimePickerDual from '../../../../components/TimePicker/TimePicker';
import { formatDateTimeNotDate } from '../../../../helpers/dates';
import Button from '../../../../components/Common/Button/Button';
import Header from '../../../../components/Common/Header/Header';
import airlinesData from '../../../../data/airlines.json';
import { updateTransferTrips } from '../../../../actions/reservationActions';

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <div className='error-label text-center mb-3' style={{ position: 'relative' }}>
      {message}
    </div>
  );
};

const getTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
};

const FlightSection = ({
  airline,
  setAirline,
  flightNumber,
  setFlightNumber,
  date,
  setDate,
  hour,
  setHour,
  minute,
  setMinute,
  t,
  getFormattedTime,
  errors = {},
  showFlightInputs = true,
  time,
  setTime,
  isInboundTrip = false,
  minDate,
  minTime,
}) => {
  const [airlinesOptions, setAirlinesOptions] = useState([]);

  useEffect(() => {
    if (showFlightInputs && airlinesData) {
      const formattedAirlines = airlinesData.map((airline, index) => ({
        value: airline.name,
        label: airline.name,
        id: index,
      }));
      setAirlinesOptions(formattedAirlines);
    }
  }, [showFlightInputs]);

  return (
    <>
      {showFlightInputs && (
        <>
          <ModalSelector
            placeholder={t('airline')}
            selectedValue={airline}
            onChange={setAirline}
            options={airlinesOptions}
          />
          <ErrorMessage message={errors.airline ? t('formErrors.airlineRequired') : ''} />

          <div className='mt-3'>
            <InputFormBorder
              placeholder={t('flightNumber')}
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
              error={errors.flightNumber ? t('formErrors.flightNumberRequired') : ''}
            />
          </div>
        </>
      )}
      <div className='mt-3'>
        <DatePickerBorder
          placeholder={
            showFlightInputs
              ? isInboundTrip
                ? t('transfer.pickupDate')
                : t('transfer.flightDate')
              : t('transfer.pickupDate')
          }
          value={date}
          onChange={setDate}
          error={errors.date ? t('formErrors.dateRequired') : ''}
          minDate={minDate}
        />
      </div>

      <div className='time-selectors-container mt-3'>
        <TimePickerDual
          value={time}
          placeholder={
            showFlightInputs
              ? isInboundTrip
                ? t('transfer.pickupTime')
                : t('transfer.flightTime')
              : t('transfer.pickupTime')
          }
          onChange={(val) => setTime(val)}
          minuteStep={5}
          enableSound
          enableVibration
          volume={0.25}
          minIntervalMs={80}
          disabled={!date}
          wheelMode='normal'
          minHour={
            date && minDate && new Date(date).toDateString() === new Date(minDate).toDateString()
              ? minTime?.hour
              : null
          }
          minMinute={
            date && minDate && new Date(date).toDateString() === new Date(minDate).toDateString()
              ? minTime?.minute
              : null
          }
        />
      </div>
      <ErrorMessage message={errors.time ? t('formErrors.timeRequired') : ''} />
      {getFormattedTime(hour, minute) && (
        <div className='mt-2 text-center'>
          <div className='font-tiny'>
            {t('selectedTime')}: {getFormattedTime(hour, minute)}
          </div>
        </div>
      )}
    </>
  );
};

const CompleteTransferDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const reservation = useSelector((state) => state.reservation);
  const [arrivalAirline, setArrivalAirline] = useState(null);
  const [arrivalFlightNumber, setArrivalFlightNumber] = useState('');
  const [arrivalDate, setArrivalDate] = useState(null);
  const [arrivalHour, setArrivalHour] = useState(null);
  const [arrivalMinute, setArrivalMinute] = useState(null);
  const [departureAirline, setDepartureAirline] = useState(null);
  const [departureFlightNumber, setDepartureFlightNumber] = useState('');
  const [departureDate, setDepartureDate] = useState(null);
  const [departureHour, setDepartureHour] = useState(null);
  const [departureMinute, setDepartureMinute] = useState(null);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({ arrival: {}, departure: {} });
  const transfer = reservation.transfer;
  const isRoundTrip = transfer?.transferType === 'ROUNDTRIP';
  const [time, setTime] = useState();
  const [timeDeparture, setTimeDeparture] = useState();
  const [globalErrorDate, setGlobalErrorDate] = useState('');

  const rawMinTime = reservation?.calendar?.minTime;
  const minDate = reservation?.calendar?.minDate;
  let minTime = null;
  if (rawMinTime) {
    const [h, m] = rawMinTime.split(':').map(Number);
    minTime = { hour: h, minute: m };
  }

  const hasAirport =
    transfer?.trips[0]?.fromPointType === 'AIRPORT' ||
    transfer?.trips[0]?.toPointType === 'AIRPORT';
  const showDepartureFlightInputs = isRoundTrip && hasAirport;

  const handleArrivalDateChange = (selected) => {
    setArrivalDate(selected);

    if (departureDate && new Date(selected) > new Date(departureDate)) {
      setDepartureDate(null);
      setTimeDeparture(null);
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = { arrival: {}, departure: {} };

    if (hasAirport) {
      if (!arrivalAirline || !arrivalAirline.value) {
        newErrors.arrival.airline = true;
        valid = false;
      }
      if (!arrivalFlightNumber) {
        newErrors.arrival.flightNumber = true;
        valid = false;
      }
    }
    if (!arrivalDate) {
      newErrors.arrival.date = true;
      valid = false;
    }
    if (!time) {
      newErrors.arrival.time = true;
      valid = false;
    }
    if (isRoundTrip) {
      if (showDepartureFlightInputs) {
        if (!departureAirline || !departureAirline.value) {
          newErrors.departure.airline = true;
          valid = false;
        }
        if (!departureFlightNumber) {
          newErrors.departure.flightNumber = true;
          valid = false;
        }
      }
      if (!departureDate) {
        newErrors.departure.date = true;
        valid = false;
      }
      if (!timeDeparture) {
        newErrors.departure.time = true;
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const getFormattedTime = (hour, minute) => {
    if (!hour || minute == null) return '';
    const [h, meridiem] = hour.value.split('-');
    return `${h}:${minute.label} ${meridiem}`;
  };

  const handleContinue = () => {
    if (!validateForm()) return;
    if (isRoundTrip && arrivalDate && departureDate) {
      const arrivalDateTime = new Date(
        `${arrivalDate}T${time.hour.toString().padStart(2, '0')}:${time.minute
          .toString()
          .padStart(2, '0')}`,
      );
      const departureDateTime = new Date(
        `${departureDate}T${timeDeparture.hour.toString().padStart(2, '0')}:${timeDeparture.minute
          .toString()
          .padStart(2, '0')}`,
      );

      if (departureDateTime < arrivalDateTime) {
        setGlobalErrorDate(t('formErrors.departureCannotBeBeforeArrival'));
        return;
      }
    }

    const updatedTrips = [...transfer.trips];
    updatedTrips[0] = {
      ...updatedTrips[0],
      transferDate: formatDateTimeNotDate(
        arrivalDate,
        time.hour.toString().padStart(2, '0') + ':' + time.minute.toString().padStart(2, '0'),
      ),
      airline: hasAirport ? arrivalAirline?.value || arrivalAirline : undefined,
      flightNumber: hasAirport ? arrivalFlightNumber : undefined,
    };
    if (isRoundTrip && updatedTrips.length > 1) {
      updatedTrips[1] = {
        ...updatedTrips[1],
        transferDate: formatDateTimeNotDate(
          departureDate,
          timeDeparture.hour.toString().padStart(2, '0') +
            ':' +
            timeDeparture.minute.toString().padStart(2, '0'),
        ),
        airline: showDepartureFlightInputs
          ? departureAirline?.value || departureAirline
          : undefined,
        flightNumber: showDepartureFlightInputs ? departureFlightNumber : undefined,
      };
    }

    dispatch(updateTransferTrips(updatedTrips));

    history.push('/form-check-transfer');
  };

  return (
    <>
      <Header />

      <ContentApp classes='pb-0 fade-up'>
        <div>
          <div className='fw-600 font-small'>
            {hasAirport ? t('arrivalInformation') : t('transfer.outboundTransfer')}
          </div>

          <div
            className='font-tiny text-tertiary mt-1'
            dangerouslySetInnerHTML={{
              __html: hasAirport
                ? t('transfer.instructionsArrivalFlight')
                : t('transfer.serviceDetails', {
                    pickup: transfer?.trips[0]?.fromPickupName,
                  }),
            }}
          />

          <div className='mt-3'>
            <FlightSection
              title='arrivalInformation'
              airline={arrivalAirline}
              setAirline={setArrivalAirline}
              flightNumber={arrivalFlightNumber}
              setFlightNumber={setArrivalFlightNumber}
              date={arrivalDate}
              setDate={handleArrivalDateChange}
              hour={arrivalHour}
              setHour={setArrivalHour}
              minute={arrivalMinute}
              setMinute={setArrivalMinute}
              t={t}
              getFormattedTime={getFormattedTime}
              errors={errors.arrival}
              showFlightInputs={hasAirport}
              time={time}
              setTime={setTime}
              minDate={minDate}
              minTime={minTime}
            />
          </div>

          {isRoundTrip && (
            <>
              <div className='fw-600 font-small mt-4'>
                {hasAirport ? t('departureInformation') : t('transfer.inboundTransfer')}
              </div>

              <div
                className='font-tiny text-tertiary mt-1'
                dangerouslySetInnerHTML={{
                  __html: hasAirport
                    ? t('transfer.instructionsDepartureFlight', {
                        pickup: transfer?.trips[1]?.fromPickupName,
                      })
                    : t('transfer.serviceDetails', {
                        pickup: transfer?.trips[1]?.fromPickupName,
                      }),
                }}
              />

              <div className='mt-3'>
                <FlightSection
                  title='departureInformation'
                  airline={departureAirline}
                  setAirline={setDepartureAirline}
                  flightNumber={departureFlightNumber}
                  setFlightNumber={setDepartureFlightNumber}
                  date={departureDate}
                  setDate={setDepartureDate}
                  hour={departureHour}
                  setHour={setDepartureHour}
                  minute={departureMinute}
                  setMinute={setDepartureMinute}
                  t={t}
                  getFormattedTime={getFormattedTime}
                  errors={errors.departure}
                  showFlightInputs={showDepartureFlightInputs}
                  time={timeDeparture}
                  setTime={setTimeDeparture}
                  isInboundTrip={true}
                  minDate={arrivalDate || getTomorrow()}
                  minTime={minTime}
                />
              </div>
            </>
          )}
        </div>
      </ContentApp>

      <div className='mt-5'>
        <ContentBottom>
          <ErrorMessage message={globalErrorDate} />
          <Button
            text={t('continue')}
            size='large'
            onClick={handleContinue}
            icon={true}
            typeIcon='arrow'
            iconColor='#000'
            disabled={false}
          />
        </ContentBottom>
      </div>
    </>
  );
};

export default CompleteTransferDetails;
